import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, uploadFile } from '../shared/api';

/**
 * m.16 Promet povezanih društava (Slovenija, Bugarska, buduće) — mail UID 471/473.
 * Ručni dnevni unos ILI uvoz periodičnog "Izvještaj prodaje" xlsx-a, plus P&L-lite analitika
 * (mjesečni trend, realizacija plana, RUC marža, YoY, mix po kanalu).
 *
 * Backend: RelatedCompanySalesController (routes/api.php, prefix /related-sales).
 * Shema generička po company_id — isti ekran poslužuje i Bugarsku čim dobije kanal-katalog.
 *
 * Vizualni stil usklađen s ostatkom BIS-a (poslovniceModule.tsx / kycCharts.tsx): kartice
 * s gornjim naglasnim rubom, --ad-acc/--ad-ink varijable umjesto tvrdo upisanih boja.
 */

type Company = { id: number; code: string; name: string; country_code: string };
type Channel = { id: number; key: string; label: string; ruc_rate: string | null; sort_order: number; active: boolean | number };
type DailyRow = {
    id: number; date: string; channel_id: number; channel_key: string; channel_label: string;
    branch: string | null; revenue_actual: string; revenue_plan: string; ruc_actual: string;
    ruc_manual_override: boolean | number; ruc_plan: string; source: string; import_batch_id: number | null;
};
type MonthRow = {
    month: number; revenue_actual: number; revenue_plan: number; revenue_realization_pct: number | null;
    ruc_actual: number; ruc_plan: number; ruc_margin_pct: number | null;
};
type ChannelMix = { key: string; label: string; revenue: number; ruc: number; revenue_share_pct: number | null };
type Analytics = {
    company_id: number; year: number;
    ytd: {
        revenue_actual: number; revenue_plan: number; ruc_actual: number; ruc_plan: number;
        revenue_realization_pct: number | null; ruc_margin_pct: number | null;
        yoy_prev_year_revenue: number; yoy_pct: number | null;
    };
    monthly: MonthRow[];
    channel_mix: ChannelMix[];
};
type CompanyBreakdown = {
    company_id: number; code: string; name: string; country_code: string;
    revenue_actual: number; revenue_plan: number; revenue_realization_pct: number | null;
    ruc_actual: number; ruc_plan: number; ruc_margin_pct: number | null;
};
type Consolidated = {
    year: number;
    totals: {
        revenue_actual: number; revenue_plan: number; ruc_actual: number; ruc_plan: number;
        revenue_realization_pct: number | null; ruc_margin_pct: number | null;
        yoy_prev_year_revenue: number; yoy_pct: number | null;
    };
    monthly: MonthRow[];
    by_company: CompanyBreakdown[];
};

const MONTHS_HR = ['Sij', 'Velj', 'Ožu', 'Tra', 'Svi', 'Lip', 'Srp', 'Kol', 'Ruj', 'Lis', 'Stu', 'Pro'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Tema (ista paleta kao poslovniceModule.tsx) ─────────────────────────────
const GOLD = 'var(--ad-acc)';
const BROWN = 'var(--ad-ink)';
const SOFT_BORDER = 'rgba(var(--ad-soft-rgb), 0.35)';
const SOFT_BG = 'rgba(var(--ad-soft-rgb), 0.08)';
const SOFT_BAR = 'rgba(var(--ad-soft-rgb), 0.45)';
const MUTED = '#8a8a8a';
const NEG = '#b03a3a';
const POS = '#1f7a36';

function fmtNum(v: number | string | null | undefined): string {
    const n = typeof v === 'string' ? parseFloat(v) : (v ?? 0);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(v: number | null | undefined): string {
    return v === null || v === undefined ? '—' : `${v.toFixed(1)}%`;
}

// ── Ponovljivi UI dijelovi (isti obrazac kao Section/StatCard u poslovniceModule.tsx) ──
function Section({ title, icon, accent = GOLD, children }: {
    title: string; icon?: string; accent?: string; children: React.ReactNode;
}) {
    return (
        <div style={{ background: '#fff', border: `1px solid ${SOFT_BORDER}`, borderTop: `3px solid ${accent}`, borderRadius: 8, padding: '14px 16px' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, display: 'flex', alignItems: 'center', gap: 6 }}>
                {icon ? <span aria-hidden="true">{icon}</span> : null}{title}
            </h4>
            {children}
        </div>
    );
}

function Kpi({ label, value, sub, accent = GOLD }: { label: string; value: string; sub?: string; accent?: string }) {
    return (
        <div style={{ background: '#fff', border: `1px solid ${SOFT_BORDER}`, borderTop: `3px solid ${accent}`, borderRadius: 8, padding: '12px 16px', minWidth: 172, flex: '1 1 172px' }}>
            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: MUTED, marginBottom: 6, fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 21, fontWeight: 800, color: BROWN, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            {sub ? <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>{sub}</div> : null}
        </div>
    );
}

/** Grupirani stupci plan/stvarno po mjesecu — isti vizualni jezik kao kycCharts.tsx (numerička oznaka iznad, mjesec ispod). */
function TrendChart({ months, monthly, planLabel, actualLabel }: {
    months: string[]; monthly: MonthRow[]; planLabel: string; actualLabel: string;
}) {
    const max = Math.max(1, ...monthly.map((m) => Math.max(m.revenue_actual, m.revenue_plan)));
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 190, borderBottom: `1px solid ${SOFT_BORDER}`, padding: '10px 4px 0' }}>
                {monthly.map((m) => (
                    <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', justifyContent: 'center', height: '100%', width: '100%' }}>
                            <div title={`${planLabel}: ${fmtNum(m.revenue_plan)}`}
                                style={{ width: '36%', maxWidth: 16, height: `${Math.max((m.revenue_plan / max) * 100, m.revenue_plan > 0 ? 2 : 0)}%`, background: SOFT_BAR, borderRadius: '4px 4px 0 0' }} />
                            <div title={`${actualLabel}: ${fmtNum(m.revenue_actual)}`}
                                style={{ width: '36%', maxWidth: 16, height: `${Math.max((m.revenue_actual / max) * 100, m.revenue_actual > 0 ? 2 : 0)}%`, background: GOLD, borderRadius: '4px 4px 0 0' }} />
                        </div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '6px 4px 0' }}>
                {monthly.map((m) => (
                    <div key={`l-${m.month}`} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: MUTED, fontWeight: 600 }}>{months[m.month - 1]}</div>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11.5, color: '#5c6b7f', fontWeight: 600 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: SOFT_BAR }} />{planLabel}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: GOLD }} />{actualLabel}</span>
            </div>
        </div>
    );
}

export function RelatedCompanySalesModule({ locale, token, onBack }: {
    locale: 'hr' | 'en';
    token: string | null;
    onBack?: () => void;
}) {
    const t = useCallback((hr: string, en: string) => (locale === 'en' ? en : hr), [locale]);
    const months = locale === 'en' ? MONTHS_EN : MONTHS_HR;

    const [tab, setTab] = useState<'entry' | 'analytics' | 'consolidated'>('entry');
    const [companies, setCompanies] = useState<Company[]>([]);
    const [companyId, setCompanyId] = useState<number | null>(null);
    const [channels, setChannels] = useState<Channel[]>([]);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api<{ companies: Company[] }>('/related-sales/companies', token)
            .then((d) => {
                setCompanies(d.companies);
                if (d.companies.length > 0) setCompanyId(d.companies[0].id);
            })
            .catch((e) => setMsg(String(e.message ?? e)));
    }, [token]);

    useEffect(() => {
        if (!companyId) return;
        api<{ channels: Channel[] }>(`/related-sales/channels?company_id=${companyId}`, token)
            .then((d) => setChannels(d.channels))
            .catch((e) => setMsg(String(e.message ?? e)));
    }, [companyId, token]);

    return (
        <div style={{ padding: '14px 18px 24px', color: '#2f3337' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                {onBack ? <button onClick={onBack} style={btnGhost}>← {t('Natrag', 'Back')}</button> : null}
                <h2 style={{ margin: 0, fontSize: 20, color: BROWN }}>🌍 {t('Promet povezanih društava', 'Related company sales')}</h2>
                <select
                    value={companyId ?? ''}
                    onChange={(e) => setCompanyId(Number(e.target.value))}
                    style={selectStyle}
                >
                    {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} ({c.country_code})</option>
                    ))}
                </select>
            </div>

            <div style={{ display: 'flex', gap: 4, borderBottom: `2px solid ${SOFT_BORDER}`, marginBottom: 16 }}>
                {(['entry', 'analytics', 'consolidated'] as const).map((k) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            padding: '9px 16px', fontSize: 14, fontWeight: tab === k ? 700 : 500,
                            color: tab === k ? BROWN : MUTED,
                            borderBottom: tab === k ? `3px solid ${GOLD}` : '3px solid transparent',
                            marginBottom: -2,
                        }}
                    >
                        {k === 'entry' ? '📝 ' + t('Unos', 'Entry')
                            : k === 'analytics' ? '📊 ' + t('Analitika', 'Analytics')
                                : '🌐 ' + t('Konsolidirano', 'Consolidated')}
                    </button>
                ))}
            </div>

            {msg ? <div style={{ color: NEG, marginBottom: 12, fontSize: 13 }}>{msg}</div> : null}

            {companyId && tab === 'entry' ? (
                <EntryTab
                    t={t}
                    token={token}
                    companyId={companyId}
                    channels={channels}
                    busy={busy}
                    setBusy={setBusy}
                    setMsg={setMsg}
                />
            ) : null}

            {companyId && tab === 'analytics' ? (
                <AnalyticsTab t={t} token={token} companyId={companyId} months={months} setMsg={setMsg} />
            ) : null}

            {tab === 'consolidated' ? (
                <ConsolidatedTab t={t} token={token} months={months} setMsg={setMsg} />
            ) : null}
        </div>
    );
}

function EntryTab({ t, token, companyId, channels, busy, setBusy, setMsg }: {
    t: (hr: string, en: string) => string;
    token: string | null;
    companyId: number;
    channels: Channel[];
    busy: boolean;
    setBusy: (v: boolean) => void;
    setMsg: (v: string) => void;
}) {
    const today = new Date();
    const [from, setFrom] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
    const [to, setTo] = useState(() => today.toISOString().slice(0, 10));
    const [rows, setRows] = useState<DailyRow[]>([]);
    const [loading, setLoading] = useState(false);

    const [formDate, setFormDate] = useState(() => today.toISOString().slice(0, 10));
    const [formChannel, setFormChannel] = useState<number | null>(null);
    const [formRevActual, setFormRevActual] = useState('');
    const [formRevPlan, setFormRevPlan] = useState('');
    const [formRucActual, setFormRucActual] = useState('');
    const [formRucPlan, setFormRucPlan] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        api<{ rows: DailyRow[] }>(`/related-sales/daily?company_id=${companyId}&from=${from}&to=${to}`, token)
            .then((d) => setRows(d.rows))
            .catch((e) => setMsg(String(e.message ?? e)))
            .finally(() => setLoading(false));
    }, [companyId, from, to, token, setMsg]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (channels.length > 0 && formChannel === null) setFormChannel(channels[0].id); }, [channels, formChannel]);

    const selectedChannel = channels.find((c) => c.id === formChannel);
    const derivedRuc = selectedChannel?.ruc_rate && formRevActual
        ? (parseFloat(formRevActual.replace(',', '.')) * parseFloat(selectedChannel.ruc_rate)).toFixed(2)
        : null;

    const submit = async () => {
        if (!formChannel) return;
        setBusy(true); setMsg('');
        try {
            await mutate('/related-sales/daily', token, 'POST', {
                company_id: companyId,
                channel_id: formChannel,
                date: formDate,
                revenue_actual: formRevActual ? parseFloat(formRevActual.replace(',', '.')) : 0,
                revenue_plan: formRevPlan ? parseFloat(formRevPlan.replace(',', '.')) : 0,
                ...(formRucActual !== '' ? { ruc_actual: parseFloat(formRucActual.replace(',', '.')) } : {}),
                ...(formRucPlan !== '' ? { ruc_plan: parseFloat(formRucPlan.replace(',', '.')) } : {}),
            });
            setFormRevActual(''); setFormRevPlan(''); setFormRucActual(''); setFormRucPlan('');
            load();
        } catch (e: unknown) {
            setMsg(String((e as Error).message ?? e));
        }
        setBusy(false);
    };

    const [file, setFile] = useState<File | null>(null);
    const doUpload = async () => {
        if (!file) return;
        setBusy(true); setMsg('');
        try {
            const fd = new FormData();
            fd.append('company_id', String(companyId));
            fd.append('files[]', file);
            const res = await uploadFile<{ results: { filename: string; ok: boolean; rows_inserted?: number; rows_skipped?: number; error?: string }[] }>('/related-sales/import', token, fd);
            const r = res.results[0];
            setMsg(r.ok
                ? t(`Uvezeno: ${r.rows_inserted} redaka, preskočeno ${r.rows_skipped}.`, `Imported: ${r.rows_inserted} rows, skipped ${r.rows_skipped}.`)
                : t(`Greška: ${r.error}`, `Error: ${r.error}`));
            setFile(null);
            load();
        } catch (e: unknown) {
            setMsg(String((e as Error).message ?? e));
        }
        setBusy(false);
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                <div style={{ flex: '1 1 320px', minWidth: 320 }}>
                    <Section title={t('Ručni dnevni unos', 'Manual daily entry')} icon="📝">
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={labelStyle}>{t('Datum', 'Date')}
                                <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} style={inputStyle} />
                            </label>
                            <label style={labelStyle}>{t('Kanal', 'Channel')}
                                <select value={formChannel ?? ''} onChange={(e) => setFormChannel(Number(e.target.value))} style={inputStyle}>
                                    {channels.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                </select>
                            </label>
                            <label style={labelStyle}>{t('Prihod stvarno', 'Revenue actual')}
                                <input value={formRevActual} onChange={(e) => setFormRevActual(e.target.value)} style={inputStyle} placeholder="0.00" />
                            </label>
                            <label style={labelStyle}>{t('Prihod plan', 'Revenue plan')}
                                <input value={formRevPlan} onChange={(e) => setFormRevPlan(e.target.value)} style={inputStyle} placeholder="0.00" />
                            </label>
                            <label style={labelStyle}>{t('RUC stvarno', 'RUC actual')}
                                <input
                                    value={formRucActual}
                                    onChange={(e) => setFormRucActual(e.target.value)}
                                    style={inputStyle}
                                    placeholder={derivedRuc ? `${t('izvedeno', 'derived')}: ${derivedRuc}` : '0.00'}
                                />
                            </label>
                            <label style={labelStyle}>{t('RUC plan', 'RUC plan')}
                                <input value={formRucPlan} onChange={(e) => setFormRucPlan(e.target.value)} style={inputStyle} placeholder="0.00" />
                            </label>
                        </div>
                        <p style={{ fontSize: 11.5, color: MUTED, margin: '10px 0' }}>
                            {t('Ako RUC stvarno ostaviš prazno, izvodi se iz stope kanala (ako je definirana).', 'If RUC actual is left empty, it is derived from the channel rate (if defined).')}
                        </p>
                        <button onClick={submit} disabled={busy || !formChannel} style={btnPrimary}>
                            {t('Spremi redak', 'Save row')}
                        </button>
                    </Section>
                </div>

                <div style={{ flex: '1 1 280px', minWidth: 280 }}>
                    <Section title={t('Uvoz izvještaja (xlsx)', 'Import report (xlsx)')} icon="📥">
                        <p style={{ fontSize: 12, color: MUTED, margin: '0 0 10px' }}>
                            {t('Isti obrazac koji stiže mailom (Metka Bradetić) — dnevni redovi cijele godine.', 'Same template that arrives by mail — full-year daily rows.')}
                        </p>
                        <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] ?? null)} style={{ marginBottom: 10, fontSize: 13 }} />
                        <div>
                            <button onClick={doUpload} disabled={busy || !file} style={btnPrimary}>{t('Uvezi', 'Import')}</button>
                        </div>
                    </Section>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: BROWN, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Pregled', 'Overview')}</h4>
                <label style={{ fontSize: 12 }}>{t('Od', 'From')} <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: 140 }} /></label>
                <label style={{ fontSize: 12 }}>{t('Do', 'To')} <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: 140 }} /></label>
                {loading ? <span style={{ fontSize: 12, color: MUTED }}>{t('učitavam…', 'loading…')}</span> : null}
            </div>
            <div style={{ overflowX: 'auto', border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                        <tr style={{ background: SOFT_BG, textAlign: 'right' }}>
                            <th style={{ ...th, textAlign: 'left' }}>{t('Datum', 'Date')}</th>
                            <th style={{ ...th, textAlign: 'left' }}>{t('Kanal', 'Channel')}</th>
                            <th style={th}>{t('Prihod stv.', 'Rev. actual')}</th>
                            <th style={th}>{t('Prihod plan', 'Rev. plan')}</th>
                            <th style={th}>{t('RUC stv.', 'RUC actual')}</th>
                            <th style={th}>{t('RUC plan', 'RUC plan')}</th>
                            <th style={{ ...th, textAlign: 'left' }}>{t('Izvor', 'Source')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={{ borderTop: `1px solid ${SOFT_BORDER}` }}>
                                <td style={td}>{r.date}</td>
                                <td style={td}>{r.channel_label}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(r.revenue_actual)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(r.revenue_plan)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(r.ruc_actual)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(r.ruc_plan)}</td>
                                <td style={td}>{r.source}{r.ruc_manual_override ? ' · RUC ' + t('ručno', 'manual') : ''}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && !loading ? (
                            <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: MUTED }}>{t('Nema podataka za odabrani period.', 'No data for selected period.')}</td></tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AnalyticsTab({ t, token, companyId, months, setMsg }: {
    t: (hr: string, en: string) => string;
    token: string | null;
    companyId: number;
    months: string[];
    setMsg: (v: string) => void;
}) {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [data, setData] = useState<Analytics | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        api<Analytics>(`/related-sales/analytics?company_id=${companyId}&year=${year}`, token)
            .then(setData)
            .catch((e) => setMsg(String(e.message ?? e)))
            .finally(() => setLoading(false));
    }, [companyId, year, token, setMsg]);

    if (!data) {
        return <div style={{ fontSize: 13, color: MUTED }}>{loading ? t('učitavam…', 'loading…') : ''}</div>;
    }

    const yoyAccent = data.ytd.yoy_pct === null ? MUTED : data.ytd.yoy_pct < 0 ? NEG : POS;

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                <label style={{ fontSize: 13 }}>{t('Godina', 'Year')}
                    <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ ...inputStyle, width: 90, marginLeft: 6 }} />
                </label>
                {loading ? <span style={{ fontSize: 12, color: MUTED }}>{t('učitavam…', 'loading…')}</span> : null}
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
                <Kpi label={t('Prihod YTD', 'Revenue YTD')} value={fmtNum(data.ytd.revenue_actual) + ' €'}
                    sub={t('plan ', 'plan ') + fmtNum(data.ytd.revenue_plan) + ' € · ' + fmtPct(data.ytd.revenue_realization_pct)} accent={GOLD} />
                <Kpi label={t('RUC YTD', 'RUC YTD')} value={fmtNum(data.ytd.ruc_actual) + ' €'}
                    sub={t('marža ', 'margin ') + fmtPct(data.ytd.ruc_margin_pct)} accent="var(--ad-acc-3)" />
                <Kpi label="YoY" value={fmtPct(data.ytd.yoy_pct)}
                    sub={t('prošla god. ', 'prev. year ') + fmtNum(data.ytd.yoy_prev_year_revenue) + ' €'} accent={yoyAccent} />
            </div>

            <Section title={t('Mjesečni trend — stvarno vs plan', 'Monthly trend — actual vs plan')} icon="📈">
                <TrendChart months={months} monthly={data.monthly} planLabel={t('plan', 'plan')} actualLabel={t('stvarno', 'actual')} />
            </Section>

            <div style={{ height: 20 }} />

            <div style={{ overflowX: 'auto', border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, marginBottom: 20, background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                        <tr style={{ background: SOFT_BG, textAlign: 'right' }}>
                            <th style={{ ...th, textAlign: 'left' }}>{t('Mjesec', 'Month')}</th>
                            <th style={th}>{t('Prihod stv.', 'Rev. actual')}</th>
                            <th style={th}>{t('Prihod plan', 'Rev. plan')}</th>
                            <th style={th}>{t('Realizacija', 'Realization')}</th>
                            <th style={th}>RUC</th>
                            <th style={th}>{t('RUC marža', 'RUC margin')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.monthly.map((m) => (
                            <tr key={m.month} style={{ borderTop: `1px solid ${SOFT_BORDER}` }}>
                                <td style={td}>{months[m.month - 1]}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(m.revenue_actual)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(m.revenue_plan)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtPct(m.revenue_realization_pct)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(m.ruc_actual)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtPct(m.ruc_margin_pct)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Section title={t('Mix po kanalu (YTD)', 'Channel mix (YTD)')} icon="🧭">
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                            <tr style={{ background: SOFT_BG, textAlign: 'right' }}>
                                <th style={{ ...th, textAlign: 'left' }}>{t('Kanal', 'Channel')}</th>
                                <th style={th}>{t('Prihod', 'Revenue')}</th>
                                <th style={th}>{t('Udio', 'Share')}</th>
                                <th style={th}>RUC</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.channel_mix.map((c) => (
                                <tr key={c.key} style={{ borderTop: `1px solid ${SOFT_BORDER}` }}>
                                    <td style={td}>{c.label}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(c.revenue)}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtPct(c.revenue_share_pct)}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(c.ruc)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>
        </div>
    );
}

function ConsolidatedTab({ t, token, months, setMsg }: {
    t: (hr: string, en: string) => string;
    token: string | null;
    months: string[];
    setMsg: (v: string) => void;
}) {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [data, setData] = useState<Consolidated | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        api<Consolidated>(`/related-sales/consolidated?year=${year}`, token)
            .then(setData)
            .catch((e) => setMsg(String(e.message ?? e)))
            .finally(() => setLoading(false));
    }, [year, token, setMsg]);

    if (!data) {
        return <div style={{ fontSize: 13, color: MUTED }}>{loading ? t('učitavam…', 'loading…') : ''}</div>;
    }

    const yoyAccent = data.totals.yoy_pct === null ? MUTED : data.totals.yoy_pct < 0 ? NEG : POS;

    return (
        <div>
            <p style={{ fontSize: 12, color: MUTED, margin: '0 0 14px' }}>
                {t('Zbroj i usporedba prihoda/RUC-a preko svih povezanih društava zajedno (trenutno Slovenija; Bugarska se pridružuje čim ima prvi izvještaj).',
                    'Sum and comparison of revenue/RUC across all related companies together (currently Slovenia; Bulgaria joins once its first report arrives).')}
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                <label style={{ fontSize: 13 }}>{t('Godina', 'Year')}
                    <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ ...inputStyle, width: 90, marginLeft: 6 }} />
                </label>
                {loading ? <span style={{ fontSize: 12, color: MUTED }}>{t('učitavam…', 'loading…')}</span> : null}
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
                <Kpi label={t('Prihod YTD — sva društva', 'Revenue YTD — all companies')} value={fmtNum(data.totals.revenue_actual) + ' €'}
                    sub={t('plan ', 'plan ') + fmtNum(data.totals.revenue_plan) + ' € · ' + fmtPct(data.totals.revenue_realization_pct)} accent={GOLD} />
                <Kpi label={t('RUC YTD — sva društva', 'RUC YTD — all companies')} value={fmtNum(data.totals.ruc_actual) + ' €'}
                    sub={t('marža ', 'margin ') + fmtPct(data.totals.ruc_margin_pct)} accent="var(--ad-acc-3)" />
                <Kpi label="YoY" value={fmtPct(data.totals.yoy_pct)}
                    sub={t('prošla god. ', 'prev. year ') + fmtNum(data.totals.yoy_prev_year_revenue) + ' €'} accent={yoyAccent} />
            </div>

            <Section title={t('Mjesečni trend — kombinirano, stvarno vs plan', 'Monthly trend — combined, actual vs plan')} icon="📈">
                <TrendChart months={months} monthly={data.monthly} planLabel={t('plan', 'plan')} actualLabel={t('stvarno', 'actual')} />
            </Section>

            <div style={{ height: 20 }} />

            <Section title={t('Breakdown po društvu (YTD)', 'Breakdown by company (YTD)')} icon="🏢">
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                            <tr style={{ background: SOFT_BG, textAlign: 'right' }}>
                                <th style={{ ...th, textAlign: 'left' }}>{t('Društvo', 'Company')}</th>
                                <th style={th}>{t('Prihod stv.', 'Rev. actual')}</th>
                                <th style={th}>{t('Prihod plan', 'Rev. plan')}</th>
                                <th style={th}>{t('Realizacija', 'Realization')}</th>
                                <th style={th}>RUC</th>
                                <th style={th}>{t('RUC marža', 'RUC margin')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.by_company.map((c) => (
                                <tr key={c.company_id} style={{ borderTop: `1px solid ${SOFT_BORDER}` }}>
                                    <td style={td}>{c.name} ({c.country_code})</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(c.revenue_actual)}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(c.revenue_plan)}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtPct(c.revenue_realization_pct)}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(c.ruc_actual)}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{fmtPct(c.ruc_margin_pct)}</td>
                                </tr>
                            ))}
                            {data.by_company.length === 0 ? (
                                <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: MUTED }}>{t('Nema podataka za odabranu godinu.', 'No data for selected year.')}</td></tr>
                            ) : null}
                            <tr style={{ borderTop: `2px solid ${SOFT_BAR}`, fontWeight: 700 }}>
                                <td style={td}>{t('Ukupno', 'Total')}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(data.totals.revenue_actual)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(data.totals.revenue_plan)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtPct(data.totals.revenue_realization_pct)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNum(data.totals.ruc_actual)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtPct(data.totals.ruc_margin_pct)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Section>
        </div>
    );
}

const btnGhost: React.CSSProperties = { background: '#fff', color: BROWN, border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13 };
const btnPrimary: React.CSSProperties = { background: GOLD, color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700 };
const selectStyle: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${SOFT_BORDER}`, fontSize: 13, background: '#fff' };
const inputStyle: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: `1px solid ${SOFT_BORDER}`, fontSize: 13, width: '100%', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { fontSize: 11.5, color: MUTED, display: 'flex', flexDirection: 'column', gap: 3 };
const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '.03em' };
const td: React.CSSProperties = { padding: '7px 10px' };
